/**
 * One admin audit-log line (Django AuditEvent) worded for people.
 *
 * The server stores only facts -- a dotted `action`, the target's name as it
 * was then, and a little `data` -- so the wording lives here, in one place,
 * for every screen that shows the log. Returns { icon, text, to }: `to` is
 * where the target can be opened, when it still makes sense to.
 *
 * The cabinet's copy of the staff panel's wording: same lines, but links go
 * to the cabinet's own pages, and anything the cabinet has no page for
 * (keys, managers, people, accounts) is not a link.
 */
const ACCESS = { admin: "administrator", read: "oʻqish" };
const MODE = { qr: "QR", ticket: "Ticket" };

export function describeEvent(e) {
  const d = e.data || {};
  const who = e.target_label || e.target_id || "";
  // One client per cabinet: no need to name it on every line.
  const inClient = (text) => text.charAt(0).toUpperCase() + text.slice(1);
  const clientLink = null;

  switch (e.action) {
    case "client.create":
      return { icon: "tabler:server-2", text: `Mijoz tizimini yaratdi — ${who}`, to: clientLink };
    case "client.rename":
      return { icon: "tabler:pencil", text: `Mijoz tizimi nomi: ${d.from} → ${d.to}`, to: clientLink };
    case "client.activate":
      return { icon: "tabler:plug", text: `${who} — yoqildi`, to: clientLink };
    case "client.deactivate":
      return { icon: "tabler:plug-off", text: `${who} — oʻchirildi`, to: clientLink };
    case "client.auth_mode":
      return {
        icon: "tabler:switch-horizontal",
        text: `${who}: kirish usuli ${MODE[d.from] || d.from} → ${MODE[d.to] || d.to}` +
          (d.sessions_ended ? ` (${d.sessions_ended} sessiya tugatildi)` : ""),
        to: clientLink,
      };
    case "origin.add":
      return { icon: "tabler:world-plus", text: inClient(`manba qoʻshildi — ${who}`), to: "/domenlar" };
    case "origin.remove":
      return { icon: "tabler:world-minus", text: inClient(`manba olib tashlandi — ${who}`), to: "/domenlar" };
    case "credential.issue":
      return { icon: "tabler:key", text: inClient(`API kaliti chiqarildi${who ? ` — ${who}` : ""}`), to: null };
    case "credential.revoke":
      return { icon: "tabler:key-off", text: inClient(`API kaliti bekor qilindi${who ? ` — ${who}` : ""}`), to: null };
    case "member.add":
      return { icon: "tabler:user-plus", text: inClient(`ishtirokchi qoʻshildi — ${who}${d.group ? ` (${d.group})` : ""}`), to: "/ishtirokchilar" };
    case "member.remove":
      return { icon: "tabler:user-minus", text: inClient(`ishtirokchi olib tashlandi — ${who}`), to: "/ishtirokchilar" };
    case "manager.grant":
      return { icon: "tabler:user-shield", text: inClient(`kabinet menejeri qoʻshildi — ${who} (${ACCESS[d.access] || d.access})`), to: null };
    case "manager.access":
      return { icon: "tabler:user-shield", text: inClient(`menejer huquqi: ${who} → ${ACCESS[d.access] || d.access}`), to: null };
    case "manager.revoke":
      return { icon: "tabler:user-x", text: inClient(`kabinet menejeri olib tashlandi — ${who}`), to: null };
    case "group.create":
      return { icon: "tabler:folder-plus", text: inClient(`guruh yaratildi — ${who}`), to: "/guruhlar" };
    case "group.delete":
      return { icon: "tabler:folder-minus", text: inClient(`guruh oʻchirildi — ${who}`), to: "/guruhlar" };
    case "thread.create":
      return { icon: "tabler:message-plus", text: inClient(`muhokama ochildi — ${who}`), to: `/muhokamalar/${e.target_id}` };
    case "identity.revoke_device":
      return { icon: "tabler:device-mobile-off", text: `${who}: bitta telefondan chiqarildi`, to: null };
    case "identity.revoke_devices":
      return { icon: "tabler:device-mobile-off", text: `${who}: hamma telefonlardan chiqarildi (${d.affected ?? 0})`, to: null };
    case "identity.end_widget_sessions":
      return { icon: "tabler:browser-x", text: `${who}: vidjetdan chiqarildi (${d.affected ?? 0})`, to: null };
    case "user.create":
      return { icon: "tabler:user-plus", text: `Hisob ochildi — ${who}` };
    case "user.activate":
      return { icon: "tabler:user-check", text: `Hisob yoqildi — ${who}` };
    case "user.deactivate":
      return { icon: "tabler:user-off", text: `Hisob oʻchirildi — ${who}` };
    case "user.role":
      return { icon: "tabler:arrow-down", text: `Rol oʻzgardi — ${who}: ichki xodim → mijoz` };
    case "user.email":
      return { icon: "tabler:mail", text: `Pochta oʻzgardi — ${who}: ${d.from || "—"} → ${d.to || "—"}` };
    case "user.end_sessions":
      return { icon: "tabler:logout", text: `Sessiyalar tugatildi — ${who} (${d.affected ?? 0})` };
    case "auth.login":
      return { icon: "tabler:login-2", text: d.method === "hidden" ? "Yashirin kirish (qurilma)" : "OneID orqali kirdi" };
    default:
      return { icon: "tabler:point", text: `${e.action}${who ? ` — ${who}` : ""}` };
  }
}

/** The Faoliyat filter: the action code's prefix. */
export const ACTIVITY_KINDS = [
  ["", "Barcha amallar"],
  ["client", "Mijoz tizimlari"],
  ["member", "Aʼzolar"],
  ["manager", "Kabinet menejerlari"],
  ["origin", "Manbalar"],
  ["credential", "API kalitlari"],
  ["group", "Guruhlar"],
  ["thread", "Muhokamalar"],
  ["identity", "Shaxslar"],
  ["user", "Hisoblar"],
];
